import { AnyAction } from '@reduxjs/toolkit';
import { assistRequestAssigned } from '../../redux/features/assistRequest/assistRequest.slice';
import {
  AssistPoolData,
  ASSIST_SCHEMA_VERSION,
  IAssistDataBase,
  IAssistEnvelope,
} from './types';

/**
 * HITL-1792 (HITL 8.0.0): task-router's assist pool events, as they arrive on
 * the agent's existing `TASK_CHANNEL_<user id>` socket (contract §4.4).
 *
 * Pure: `routeAssistPoolEvent` turns one parsed message into the slice action
 * to dispatch, or says why it was ignored. The hook owns the socket, the clock
 * and the logging.
 */

export const AssistPoolEvents = {
  assigned: 'assist_request_assigned',
} as const;

/**
 * The types this console has a card body for. An assignment of any other
 * type is ignored (contract D5): task-router may register a type before this
 * console ships its card.
 */
export const RENDERABLE_ASSIST_TYPES: ReadonlySet<string> = new Set([
  'hear_and_confirm',
]);

const SCHEMA_MAJOR = ASSIST_SCHEMA_VERSION.split('.')[0];

/** Same major as ours; a newer minor only adds optional fields. */
export const isSupportedSchemaVersion = (version: unknown): boolean =>
  typeof version === 'string' && version.split('.')[0] === SCHEMA_MAJOR;

export type AssistPoolIgnoreReason =
  /** Not an assist pool event: the task feed's, left to useTaskRouterWS. */
  | 'not_pool_event'
  | 'unknown_event'
  | 'unsupported_schema'
  | 'malformed'
  | 'unrenderable_type'
  /** An order task is current: an order always beats an assist card. */
  | 'task_current';

export type AssistPoolRoute =
  | { action: AnyAction }
  | { ignored: AssistPoolIgnoreReason };

const POOL_EVENT_NAMES: ReadonlySet<string> = new Set(
  Object.values(AssistPoolEvents)
);

export const routeAssistPoolEvent = (
  message: IAssistEnvelope<AssistPoolData<IAssistDataBase>> | null,
  receivedAt: number,
  { hasCurrentTask = false }: { hasCurrentTask?: boolean } = {}
): AssistPoolRoute => {
  const event = message?.event;
  if (typeof event !== 'string' || !event.startsWith('assist_')) {
    return { ignored: 'not_pool_event' };
  }
  if (!POOL_EVENT_NAMES.has(event)) return { ignored: 'unknown_event' };

  const data: any = message?.data;
  if (!data || !isSupportedSchemaVersion(data.schema_version)) {
    return { ignored: 'unsupported_schema' };
  }
  if (!data.request_id) return { ignored: 'malformed' };

  if (
    !data.store_restaurant_code ||
    !message?.session_id ||
    !data.request ||
    typeof data.remaining_ms !== 'number'
  ) {
    return { ignored: 'malformed' };
  }
  if (!RENDERABLE_ASSIST_TYPES.has(data.type)) {
    return { ignored: 'unrenderable_type' };
  }
  // An assignment that lands after an order offer must not open a card over
  // the order; unanswered, it is simply one answer fewer.
  if (hasCurrentTask) return { ignored: 'task_current' };
  return {
    action: assistRequestAssigned({
      data,
      receivedAt,
      sessionId: message.session_id,
      restaurantCode: data.store_restaurant_code,
      laneId: data.lane_id,
    }),
  };
};
